import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import { lighten } from 'polished';
import Theme from '../common/NekoTheme';
import { IconifyIcon } from '@iconify/react';
import { NekoSpinner } from '../misc/Spinner';
import { NekoIcon } from '../misc/Icon';

const StyledButton = Styled(props => {

  const { className, icon = null, onClick, disabled = false, color = null, small, isBusy = false, width, height,
    children, rounded, busyText, spinning = false, hideBusyIcon = false, busyIconSize, ...rest } = props;
  const hasIcon = !!icon;
  const iconWidth = useMemo(() => {
    const base = width ?? 30;
    return rounded ? base - 10 : base - 6;
  }, [width, rounded]);
  const spinnerSize = useMemo(() => {
    if (busyIconSize) return busyIconSize;
    return rounded ? '82%' : '35%';
  }, [rounded, busyIconSize]);

  const classNames = `nui-button ${className || ''}${hasIcon ? ' has-icon' : ''}${color ? ' custom-color' : ''}${small ? ' small' : ''}${rounded ? ' rounded' : ''}${isBusy ? ' busy' : ''}`;

  return (
    <button type="button" className={classNames} onClick={onClick} disabled={disabled} {...rest}>
      {isBusy && !hideBusyIcon && <NekoSpinner type="circle" color="#fff" size={spinnerSize} />}
      {isBusy && !!busyText && <span style={{ marginLeft: hideBusyIcon ? 0 : '4px', marginRight: '2xp' }}>{busyText}</span>}
      {hasIcon && !isBusy && <NekoIcon icon={icon} width={iconWidth} height={iconWidth} spinning={spinning} raw style={ rounded ? { margin: '0 auto' } : {}} />}
      {!!children && !isBusy && <span style={{ marginLeft: hasIcon ? '4px' : 0, marginRight: hasIcon ? '2xp' : 0 }}>{children}</span>}
    </button>
  );
})`
  display: inline-block;
  box-sizing: border-box;
  height: 30px;
  min-height: 30px;
  min-width: 6em;
  border: none;
  border-radius: 4px;
  text-align: center;
  font-family: ${Theme.fontFamily};
  font-size: ${Theme.fontSizeText};
  padding: 0 15px;

  background-color: ${Theme.blue};
  color: white;
  transition: background-color .10s, opacity .25s;

  :not([disabled]):hover {
    cursor: pointer;
    background-color: ${lighten(0.1, Theme.blue)};
  }

  :disabled {
    opacity: 0.35;
  }

  :focus {
    outline: none;
  }

  &.has-icon {
    align-items: center;
    display: inline-flex;
    padding: 2.5px 8px;
  }

  &.secondary {
    background-color: ${Theme.lightBlue};
    border: 1px solid ${Theme.blue};
    color: ${Theme.blue};

    &:hover {
      background-color: ${lighten(0.1, Theme.lightBlue)};
    }
  }

  &.danger {
    background-color: ${Theme.red};
    border-color: ${Theme.red};

	  &:hover {
		  background-color: ${lighten(0.1, Theme.red)};
	  }
  }

  &.success {
    background-color: ${Theme.green};
    border-color: ${Theme.green};

	  &:hover {
		  background-color: ${lighten(0.1, Theme.green)};
	  }
  }

  & + button {
    margin-left: .25rem;
  }

  &.small {
    font-size: ${Theme.fontSizeSmallButton};
    height: 24px;
  }

  &.header {
    background-color: ${Theme.deepBlue};
    border-color: ${Theme.deepBlue};
    height: 40px;
    padding: 0 20px;

	  &:hover {
		  background-color: ${lighten(0.1, Theme.deepBlue)};
	  }
  }

  &.rounded {
    border-radius: 100%;
    min-width: 30px;
    height: ${props => props.height ?? (props.width ?? 30)}px;
    width: ${props => props.width ?? 30}px;
    padding: 3px;
  }

  &.busy {
    pointer-events: none;
  }

  ${props => additionalColor(props.color)}
`;

const additionalColor = (color) => {
  if (color) {
    return `
      &.custom-color {
        background-color: ${color};
        border: 1px solid ${color};

        &:hover {
          background-color: ${lighten(0.1, color)};
        }
      }
    `;
  }
};

/**
 * The NekoButton works out of the box without specific attributes.
 */
const NekoButton = (props) => {
  return (<StyledButton {...props} />);
};

NekoButton.propTypes = {
  className: PropTypes.oneOf(['primary', 'secondary', 'danger', 'success', 'header']),
  disabled: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.instanceOf(IconifyIcon), PropTypes.oneOf(['setting', 'edit', 'trash'])]),
  color: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  rounded: PropTypes.bool,
  isBusy: PropTypes.bool,
  spinning: PropTypes.bool,
  busyText: PropTypes.string,
  hideBusyIcon: PropTypes.bool,
  busyIconSize: PropTypes.string,
};

NekoButton.defaultProps = {
  className: 'primary',
  disabled: false,
  icon: null,
  color: null,
  onClick: () => {},
  rounded: undefined,
  isBusy: false,
  spinning: false,
  busyText: undefined,
  hideBusyIcon: false,
  busyIconSize: undefined,
};

export { NekoButton };
